import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { smoothScrollTo } from '../utils/scroll';
import { urlFor } from '../sanityClient';
import { useSiteSettings } from '../context/SiteSettingsContext';

export default function Navbar({ onBookClick }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { settings } = useSiteSettings();
  const logoUrl = settings?.logo ? urlFor(settings.logo).url() : null;
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const scrollToSection = (id) => {
    setIsMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;

      smoothScrollTo(offsetPosition, 900);
    }
  };

  const navLinks = [
    { label: 'Projects', target: 'portfolio' },
    { label: 'Vision',   target: 'vision'    },
    { label: 'Services', target: 'services'  },
  ];

  const menuItemVariants = {
    closed: { opacity: 0, x: -30 },
    open: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.1 + 0.2,
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1],
      },
    }),
    exit: (i) => ({
      opacity: 0,
      x: 30,
      transition: {
        delay: i * 0.05,
        duration: 0.3,
      },
    }),
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${isScrolled
            ? 'py-2 bg-[#f4ceb3]/90 backdrop-blur-md border-b border-[#9c1b42]/20'
            : 'py-4 bg-transparent'
          }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex justify-between items-center">
          {/* Brand Logo */}
          <Link
            to="/"
            onClick={() => {
              setIsMobileMenuOpen(false);
              if (isHomePage) {
                smoothScrollTo(0, 900);
              }
            }}
            className="cursor-pointer hover:opacity-80 transition-opacity z-50 relative flex items-center shrink-0"
            aria-label="Pomie Studio — Back to home"
          >
            <img
              src={logoUrl || "/logo.svg"}
              alt="Pomie Studio Official Logo"
              width="400"
              height="400"
              className={`transition-all duration-300 aspect-square object-contain shrink-0 ${
                isScrolled
                  ? 'h-20 w-20 sm:h-22 sm:w-22 md:h-24 md:w-24 lg:h-26 lg:w-26'
                  : 'h-32 w-32 sm:h-36 sm:w-36 md:h-44 md:w-44 lg:h-52 lg:w-52'
              }`}
            />
          </Link>

          {/* Desktop Navigation Links */}
          {isHomePage && (
            <nav className="hidden md:flex items-center space-x-6 md:space-x-8 lg:space-x-10">
              {navLinks.map((link) => (
                <button
                  key={link.label}
                  onClick={() => scrollToSection(link.target)}
                  className="relative text-sm md:text-base font-sans font-medium uppercase tracking-widest text-[#9c1b42]/80 hover:text-[#9c1b42] cursor-pointer group py-1 transition-colors duration-300"
                >
                  <span>{link.label}</span>
                  {/* Sliding underline on hover */}
                  <span className="absolute bottom-0 left-0 w-full h-[1.5px] bg-[#9c1b42] transform scale-x-0 origin-right transition-transform duration-300 ease-out group-hover:scale-x-100 group-hover:origin-left" />
                </button>
              ))}
              <Link
                to="/book-now"
                className="text-xs md:text-sm font-sans font-semibold uppercase tracking-widest text-[#f4ceb3] bg-[#9c1b42] border border-[#9c1b42] px-4 py-2 hover:bg-transparent hover:text-[#9c1b42] transition-colors cursor-pointer inline-block"
              >
                Book Now
              </Link>
            </nav>
          )}

          {/* Mobile Hamburger Toggle */}
          {isHomePage && (
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden relative z-50 w-8 h-8 flex flex-col justify-center items-center gap-[5px] cursor-pointer"
              aria-label="Toggle menu"
            >
              <motion.span
                className="block w-6 h-[2px] bg-[#9c1b42] origin-center"
                animate={isMobileMenuOpen ? { rotate: 45, y: 3.5 } : { rotate: 0, y: 0 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              />
              <motion.span
                className="block w-6 h-[2px] bg-[#9c1b42] origin-center"
                animate={isMobileMenuOpen ? { rotate: -45, y: -3.5 } : { rotate: 0, y: 0 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              />
            </button>
          )}
        </div>
      </header>

      {/* Mobile Full-Screen Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="fixed inset-0 z-[39] bg-[#f4ceb3] flex flex-col justify-center items-center md:hidden"
            initial={{ clipPath: 'circle(0% at calc(100% - 40px) 40px)' }}
            animate={{ clipPath: 'circle(150% at calc(100% - 40px) 40px)' }}
            exit={{ clipPath: 'circle(0% at calc(100% - 40px) 40px)' }}
            transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
          >
            {/* Background decoration */}
            <div className="absolute inset-0 pointer-events-none opacity-[0.03]"
              style={{
                backgroundImage: 'radial-gradient(#9c1b42 0.5px, transparent 0.5px)',
                backgroundSize: '20px 20px'
              }}
            />

            <nav className="flex flex-col items-center gap-8 relative z-10 w-full px-6 overflow-hidden">
              <div className="flex flex-col items-center justify-center w-full px-4 overflow-hidden gap-6">
                {navLinks.map((link, i) => (
                  <motion.button
                    key={link.label}
                    custom={i}
                    variants={menuItemVariants}
                    initial="closed"
                    animate="open"
                    exit="exit"
                    onClick={() => scrollToSection(link.target)}
                    className="w-full text-center whitespace-nowrap text-[5.5vw] min-[400px]:text-[5vw] sm:text-4xl md:text-5xl lg:text-7xl font-manrope font-extrabold uppercase tracking-tight text-[#9c1b42] cursor-pointer hover:opacity-70 transition-opacity"
                  >
                    {link.label}
                  </motion.button>
                ))}
              </div>

              {/* Book Now CTA */}
              <motion.div
                custom={navLinks.length}
                variants={menuItemVariants}
                initial="closed"
                animate="open"
                exit="exit"
                className="mt-4 flex"
              >
                <Link
                  to="/book-now"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-sm font-sans font-semibold uppercase tracking-widest text-[#f4ceb3] bg-[#9c1b42] border border-[#9c1b42] px-8 py-3 hover:bg-transparent hover:text-[#9c1b42] transition-colors cursor-pointer inline-block"
                >
                  Book Now
                </Link>
              </motion.div>

              {/* Footer info in mobile menu */}
              <motion.div
                custom={navLinks.length + 1}
                variants={menuItemVariants}
                initial="closed"
                animate="open"
                exit="exit"
                className="mt-8 text-center"
              >
                <p className="text-[10px] font-sans tracking-widest uppercase text-[#9c1b42]/50">
                  Islamabad — Rawalpindi
                </p>
                <p className="text-[10px] font-sans tracking-widest uppercase text-[#9c1b42]/40 mt-1">
                  Interior Architecture Studio
                </p>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
